import { useState, useMemo } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/lib/auth";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Progress } from "@/components/ui/progress";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import { Loader2, Eye, CheckCircle, UserCheck, AlertCircle, RefreshCw, Mail, Clock, Filter, Send } from "lucide-react";
import { format } from "date-fns";
import { ProfileReviewDialog } from "./ProfileReviewDialog";
import { SendProfileNotificationDialog } from "./SendProfileNotificationDialog";
import { SendImportedWelcomeDialog } from "./SendImportedWelcomeDialog";
import { toast } from "sonner";
import { 
  getProfileCompletionStatus, 
  getRelativeTime, 
  getLastActivityDate,
  type ProfileCompletionData,
  type ProfileCompletionResult 
} from "@/lib/profileCompletion";

interface PendingProfile {
  id: string;
  full_name: string;
  role: "entrepreneur" | "mentor";
  is_verified: boolean;
  is_published: boolean;
  created_at: string;
  updated_at: string | null;
  bio: string | null;
  gender: string | null;
  age_range: string | null;
  referred_by: string | null;
  email: string | null;
  phone: string | null;
  linkedin_url: string | null;
  country: string | null;
  private_updated_at: string | null;
  imported_at: string | null;
}

type SortOption = "newest" | "oldest" | "completion_asc" | "completion_desc" | "activity";
type FilterOption = "all" | "incomplete" | "pending_approval" | "imported_not_welcomed";

export function PendingProfilesManager() {
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const [selectedProfileId, setSelectedProfileId] = useState<string | null>(null);
  const [emailDialogOpen, setEmailDialogOpen] = useState(false);
  const [selectedProfileForEmail, setSelectedProfileForEmail] = useState<PendingProfile | null>(null);
  const [sortBy, setSortBy] = useState<SortOption>("newest");
  const [filterBy, setFilterBy] = useState<FilterOption>("all");
  const [selectedUserIds, setSelectedUserIds] = useState<Set<string>>(new Set());
  const [welcomeDialogOpen, setWelcomeDialogOpen] = useState(false);

  // Fetch pending profiles with all required fields
  const { data: pendingProfiles, isLoading, refetch } = useQuery({
    queryKey: ["pending_profiles_review"],
    queryFn: async () => {
      // Get profiles that are either not verified or not published
      const { data: profiles, error } = await supabase
        .from("profiles")
        .select("id, full_name, role, is_verified, is_published, created_at, updated_at, bio, gender, age_range, referred_by, imported_at")
        .or("is_verified.eq.false,is_published.eq.false")
        .order("created_at", { ascending: false });

      if (error) throw error;

      // Fetch private data for all profiles
      const profileIds = profiles?.map((p) => p.id) || [];
      let privateData: any[] = [];
      if (profileIds.length > 0) {
        const { data } = await supabase
          .from("profile_private")
          .select("user_id, email, phone, linkedin_url, country, updated_at")
          .in("user_id", profileIds);
        privateData = data || [];
      }

      // Fetch welcome email status for imported users
      const importedIds = profiles?.filter(p => p.imported_at).map(p => p.id) || [];
      let welcomedUserIds: Set<string> = new Set();
      if (importedIds.length > 0) {
        const { data: emailLogs } = await supabase
          .from("profile_admin_emails")
          .select("profile_id")
          .in("profile_id", importedIds)
          .eq("template_type", "welcome_imported");
        welcomedUserIds = new Set(emailLogs?.map(e => e.profile_id) || []);
      }

      // Merge data
      return profiles?.map((p) => {
        const privData = privateData.find((pd) => pd.user_id === p.id);
        return {
          ...p,
          email: privData?.email || null,
          phone: privData?.phone || null,
          linkedin_url: privData?.linkedin_url || null,
          country: privData?.country || null,
          private_updated_at: privData?.updated_at || null,
          hasReceivedWelcome: welcomedUserIds.has(p.id),
        };
      }) as (PendingProfile & { hasReceivedWelcome: boolean })[];
    },
  });

  // Calculate completion status for each profile
  const profilesWithStatus = useMemo(() => {
    if (!pendingProfiles) return [];
    
    return pendingProfiles.map((profile) => {
      const completionData: ProfileCompletionData = {
        profile: {
          full_name: profile.full_name,
          bio: profile.bio,
          gender: profile.gender,
          age_range: profile.age_range,
          referred_by: profile.referred_by,
          is_published: profile.is_published,
          updated_at: profile.updated_at,
          created_at: profile.created_at,
        },
        privateData: {
          email: profile.email,
          phone: profile.phone,
          linkedin_url: profile.linkedin_url,
          country: profile.country,
          updated_at: profile.private_updated_at,
        },
      };
      
      const completion = getProfileCompletionStatus(completionData);
      const lastActivity = getLastActivityDate(completionData);
      
      return {
        ...profile,
        completion,
        lastActivity,
        lastActivityRelative: getRelativeTime(lastActivity),
      };
    });
  }, [pendingProfiles]);

  // Filter and sort profiles
  const filteredAndSortedProfiles = useMemo(() => {
    let result = [...profilesWithStatus];
    
    // Apply filter
    if (filterBy === "incomplete") {
      result = result.filter((p) => !p.completion.isComplete);
    } else if (filterBy === "pending_approval") {
      result = result.filter((p) => p.completion.isComplete);
    } else if (filterBy === "imported_not_welcomed") {
      result = result.filter((p) => p.imported_at && !p.hasReceivedWelcome);
    }
    
    // Apply sort
    result.sort((a, b) => {
      switch (sortBy) {
        case "newest":
          return new Date(b.created_at).getTime() - new Date(a.created_at).getTime();
        case "oldest":
          return new Date(a.created_at).getTime() - new Date(b.created_at).getTime();
        case "completion_asc":
          return a.completion.completedCount - b.completion.completedCount;
        case "completion_desc":
          return b.completion.completedCount - a.completion.completedCount;
        case "activity":
          const aTime = a.lastActivity ? new Date(a.lastActivity).getTime() : 0;
          const bTime = b.lastActivity ? new Date(b.lastActivity).getTime() : 0;
          return bTime - aTime;
        default:
          return 0;
      }
    });
    
    return result;
  }, [profilesWithStatus, filterBy, sortBy]);

  // Count stats
  const incompleteCount = profilesWithStatus.filter((p) => !p.completion.isComplete).length;
  const pendingApprovalCount = profilesWithStatus.filter((p) => p.completion.isComplete).length;
  const totalPending = profilesWithStatus.length;
  const importedNotWelcomedCount = profilesWithStatus.filter((p) => p.imported_at && !p.hasReceivedWelcome).length;

  // Selection helpers
  const toggleUserSelection = (userId: string) => {
    setSelectedUserIds(prev => {
      const next = new Set(prev);
      if (next.has(userId)) {
        next.delete(userId);
      } else {
        next.add(userId);
      }
      return next;
    });
  };

  const toggleSelectAll = () => {
    if (selectedUserIds.size === filteredAndSortedProfiles.length) {
      setSelectedUserIds(new Set());
    } else {
      setSelectedUserIds(new Set(filteredAndSortedProfiles.map(p => p.id)));
    }
  };

  const selectedUsers = filteredAndSortedProfiles.filter(p => selectedUserIds.has(p.id)).map(p => ({
    id: p.id,
    full_name: p.full_name,
    email: p.email
  }));

  const getStatusBadge = (completion: ProfileCompletionResult) => {
    if (completion.isComplete) {
      return (
        <Badge className="bg-success text-success-foreground hover:bg-success/90 transition-all">
          <CheckCircle className="h-3 w-3 mr-1" />
          Pending Approval
        </Badge>
      );
    }
    return (
      <Badge className="bg-warning text-warning-foreground hover:bg-warning/90 transition-all">
        <AlertCircle className="h-3 w-3 mr-1" />
        Profile Incomplete
      </Badge>
    );
  };

  const getCompletionProgress = (completion: ProfileCompletionResult) => {
    if (completion.isComplete) return null;
    
    const percentage = Math.round((completion.completedCount / completion.totalCount) * 100);
    
    return (
      <div className="flex items-center gap-2 mt-1">
        <Progress value={percentage} className="h-1.5 w-16 bg-muted" />
        <span className="text-xs text-muted-foreground">
          {completion.completedCount}/{completion.totalCount}
        </span>
      </div>
    );
  };

  if (isLoading) {
    return (
      <Card>
        <CardContent className="flex items-center justify-center py-12">
          <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-4">
      {/* Stats */}
      <div className="grid gap-4 md:grid-cols-4">
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-2">
              <AlertCircle className="h-5 w-5 text-warning" />
              <div>
                <p className="text-2xl font-bold">{incompleteCount}</p>
                <p className="text-sm text-muted-foreground">Profile Incomplete</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-2">
              <CheckCircle className="h-5 w-5 text-success" />
              <div>
                <p className="text-2xl font-bold">{pendingApprovalCount}</p>
                <p className="text-sm text-muted-foreground">Pending Approval</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-2">
              <Send className="h-5 w-5 text-warning" />
              <div>
                <p className="text-2xl font-bold">{importedNotWelcomedCount}</p>
                <p className="text-sm text-muted-foreground">Imported, Not Welcomed</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-2">
              <Eye className="h-5 w-5 text-primary" />
              <div>
                <p className="text-2xl font-bold">{totalPending}</p>
                <p className="text-sm text-muted-foreground">Total Pending</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Pending Profiles */}
      <Card>
        <CardHeader className="flex flex-col gap-4">
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2">
            <div>
              <CardTitle className="text-lg sm:text-xl">Pending Profile Reviews</CardTitle>
              <CardDescription className="text-xs sm:text-sm">
                Review and approve user profiles before they can access the platform
              </CardDescription>
            </div>
            <Button variant="outline" size="sm" onClick={() => refetch()} className="w-full sm:w-auto">
              <RefreshCw className="h-4 w-4 mr-2" />
              Refresh
            </Button>
          </div>
          
          {/* Filters and Sort */}
          <div className="flex flex-col sm:flex-row gap-2">
            <div className="flex items-center gap-2">
              <Filter className="h-4 w-4 text-muted-foreground" />
              <Select value={filterBy} onValueChange={(v) => setFilterBy(v as FilterOption)}>
                <SelectTrigger className="w-[160px] h-8">
                  <SelectValue placeholder="Filter by status" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Profiles</SelectItem>
                  <SelectItem value="incomplete">Incomplete Only</SelectItem>
                  <SelectItem value="pending_approval">Ready for Approval</SelectItem>
                  <SelectItem value="imported_not_welcomed">Imported, Not Welcomed</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="flex items-center gap-2">
              <Clock className="h-4 w-4 text-muted-foreground" />
              <Select value={sortBy} onValueChange={(v) => setSortBy(v as SortOption)}>
                <SelectTrigger className="w-[160px] h-8">
                  <SelectValue placeholder="Sort by" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="newest">Newest First</SelectItem>
                  <SelectItem value="oldest">Oldest First</SelectItem>
                  <SelectItem value="activity">Last Activity</SelectItem>
                  <SelectItem value="completion_desc">Most Complete</SelectItem>
                  <SelectItem value="completion_asc">Least Complete</SelectItem>
                </SelectContent>
              </Select>
            </div>
            
            {/* Send Welcome Emails button */}
            {selectedUserIds.size > 0 && (
              <Button 
                variant="default" 
                size="sm" 
                onClick={() => setWelcomeDialogOpen(true)}
                className="ml-auto"
              >
                <Send className="h-4 w-4 mr-2" />
                Send Welcome ({selectedUserIds.size})
              </Button>
            )}
          </div>
        </CardHeader>
        <CardContent>
          {filteredAndSortedProfiles && filteredAndSortedProfiles.length > 0 ? (
            <>
              {/* Mobile card layout */}
              <div className="space-y-3 md:hidden">
                {filteredAndSortedProfiles.map((profile) => (
                  <div 
                    key={profile.id} 
                    className={`border rounded-lg p-3 space-y-3 hover:bg-muted/50 transition-colors ${selectedUserIds.has(profile.id) ? 'ring-2 ring-primary' : ''}`}
                  >
                    {/* Header row with checkbox and name */}
                    <div className="flex items-start gap-3">
                      <Checkbox
                        checked={selectedUserIds.has(profile.id)}
                        onCheckedChange={() => toggleUserSelection(profile.id)}
                        className="mt-1"
                      />
                      <div className="min-w-0 flex-1">
                        <p className="font-medium text-sm truncate">{profile.full_name}</p>
                        <p className="text-xs text-muted-foreground truncate">{profile.email || "No email"}</p>
                        <div className="flex items-center gap-2 mt-1 flex-wrap">
                          <Badge variant="outline" className="capitalize text-xs">
                            {profile.role}
                          </Badge>
                          {profile.imported_at && (
                            <Badge variant={profile.hasReceivedWelcome ? "secondary" : "outline"} className="text-xs">
                              {profile.hasReceivedWelcome ? "Welcomed" : "Imported"}
                            </Badge>
                          )}
                          {getCompletionProgress(profile.completion)}
                        </div>
                      </div>
                    </div>
                    
                    {/* Status and activity row */}
                    <div className="flex items-center justify-between gap-2 flex-wrap">
                      {getStatusBadge(profile.completion)}
                      <div className="flex items-center gap-1 text-xs text-muted-foreground">
                        <Clock className="h-3 w-3" />
                        {profile.lastActivityRelative}
                      </div>
                    </div>
                    
                    {/* Action buttons */}
                    <div className="flex gap-2">
                      <Button
                        variant="outline"
                        size="sm"
                        className="flex-1 h-9"
                        onClick={() => {
                          setSelectedProfileForEmail(profile);
                          setEmailDialogOpen(true);
                        }}
                        disabled={!profile.email}
                        title={!profile.email ? "No email available" : undefined}
                      >
                        <Mail className="h-4 w-4 mr-1.5" />
                        Email
                      </Button>
                      <Button
                        variant="default"
                        size="sm"
                        className="flex-1 h-9"
                        onClick={() => setSelectedProfileId(profile.id)}
                      >
                        <Eye className="h-4 w-4 mr-1.5" />
                        Review
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
              
              {/* Desktop table layout */}
              <div className="hidden md:block">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead className="w-12">
                        <Checkbox
                          checked={selectedUserIds.size === filteredAndSortedProfiles.length && filteredAndSortedProfiles.length > 0}
                          onCheckedChange={toggleSelectAll}
                        />
                      </TableHead>
                      <TableHead>Name</TableHead>
                      <TableHead>Role</TableHead>
                      <TableHead>Completion</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead>Last Activity</TableHead>
                      <TableHead className="text-right">Action</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {filteredAndSortedProfiles.map((profile) => (
                      <TableRow key={profile.id} className={selectedUserIds.has(profile.id) ? 'bg-muted/50' : ''}>
                        <TableCell>
                          <Checkbox
                            checked={selectedUserIds.has(profile.id)}
                            onCheckedChange={() => toggleUserSelection(profile.id)}
                          />
                        </TableCell>
                        <TableCell>
                          <div>
                            <p className="font-medium">{profile.full_name}</p>
                            <p className="text-xs text-muted-foreground">{profile.email || "No email"}</p>
                          </div>
                        </TableCell>
                        <TableCell>
                          <div className="flex flex-col gap-1">
                            <Badge variant="outline" className="capitalize w-fit">
                              {profile.role}
                            </Badge>
                            {profile.imported_at && (
                              <Badge variant={profile.hasReceivedWelcome ? "secondary" : "outline"} className="text-xs w-fit">
                                {profile.hasReceivedWelcome ? "Welcomed" : "Imported"}
                              </Badge>
                            )}
                          </div>
                        </TableCell>
                        <TableCell>
                          <div className="flex items-center gap-2">
                            <Progress 
                              value={(profile.completion.completedCount / profile.completion.totalCount) * 100} 
                              className="h-2 w-20"
                            />
                            <span className="text-xs text-muted-foreground whitespace-nowrap">
                              {profile.completion.completedCount}/{profile.completion.totalCount}
                            </span>
                          </div>
                        </TableCell>
                        <TableCell>
                          {getStatusBadge(profile.completion)}
                        </TableCell>
                        <TableCell>
                          <div className="flex items-center gap-1 text-sm text-muted-foreground">
                            <Clock className="h-3.5 w-3.5" />
                            {profile.lastActivityRelative}
                          </div>
                        </TableCell>
                        <TableCell className="text-right">
                          <div className="flex gap-2 justify-end">
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => {
                                setSelectedProfileForEmail(profile);
                                setEmailDialogOpen(true);
                              }}
                            >
                              <Mail className="h-4 w-4 mr-2" />
                              Email
                            </Button>
                            <Button
                              variant="default"
                              size="sm"
                              onClick={() => setSelectedProfileId(profile.id)}
                            >
                              <Eye className="h-4 w-4 mr-2" />
                              Review
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            </>
          ) : (
            <div className="text-center py-12 text-muted-foreground">
              <CheckCircle className="h-12 w-12 mx-auto mb-4 text-success" />
              <p className="text-lg font-medium">All caught up!</p>
              <p className="text-sm">
                {filterBy === "all" 
                  ? "No pending profiles to review."
                  : filterBy === "incomplete"
                    ? 'No profiles match the "Incomplete" filter.'
                    : filterBy === "pending_approval"
                      ? 'No profiles match the "Ready for Approval" filter.'
                      : 'No imported users awaiting welcome emails.'
                }
              </p>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Profile Review Dialog */}
      {selectedProfileId && (
        <ProfileReviewDialog
          profileId={selectedProfileId}
          open={!!selectedProfileId}
          onOpenChange={(open) => {
            if (!open) setSelectedProfileId(null);
          }}
          onActionComplete={() => {
            setSelectedProfileId(null);
            refetch();
          }}
        />
      )}

      {/* Send Email Dialog */}
      {selectedProfileForEmail && (
        <SendProfileNotificationDialog
          open={emailDialogOpen}
          onOpenChange={setEmailDialogOpen}
          profileId={selectedProfileForEmail.id}
          recipientEmail={selectedProfileForEmail.email || ""}
          recipientName={selectedProfileForEmail.full_name || "User"}
        />
      )}

      {/* Send Welcome Emails Dialog */}
      <SendImportedWelcomeDialog
        open={welcomeDialogOpen}
        onOpenChange={setWelcomeDialogOpen}
        selectedUsers={selectedUsers}
        onComplete={() => {
          setSelectedUserIds(new Set());
          refetch();
        }}
      />
    </div>
  );
}
